import { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { createUseStyles } from "$utils/createUseStyles";
import { Link, useLocation } from "react-router-dom";
import { Text } from "$components";
import * as Page from "$containers";
import useAuth from "$hooks/useAuth";
import EDBForm from "$containers/EDB/form/EDBForm";

import {
  DockyardServices,
  EDBService,
  ProjectServices,
  CompanyServices,
  UserServices,
  ProductService,
  EntrepotService,
  ShippingRecordService,
  OpService,
} from "$services";
import { theme } from "$utils/theme/bataillog";

interface ILink {
  path: string;
  name: string;
  component: any;
  hasAccess: boolean;
}

interface IContext {
  links: ILink[];
  routerProps: any;
}

// create breadcrumbs context
const BreadcrumbContext = createContext<IContext>({
  links: [],
  routerProps: {},
});

export const makeLinks = ({
  isAdmin,
  isClient,
  isContributor,
}: {
  isAdmin?: () => boolean;
  isClient?: () => boolean;
  isContributor?: () => boolean;
} = {}) => {
  const adminAccess =
    typeof isAdmin === "function" ? Boolean(isAdmin()) : false;
  const clientAccess =
    typeof isClient === "function" ? Boolean(isClient()) : false;
  const contributorAccess =
    typeof isContributor === "function" ? Boolean(isContributor()) : false;

  return [
    {
      path: "/edbs",
      component: Page.EDB,
      name: "Liste des expressions de besoin",
      hasAccess: true,
    },
    {
      path: "/edbs/:idEdb/chantiers/:idChantier/",
      component: Page.DockyardDetails,
      name: "",
      hasAccess: true,
    }, // new link 25/07/22
    {
      path: "/edbs/:idEdb/projets/:idProject",
      component: Page.ProjectDetails,
      name: "",
      hasAccess: true,
    }, // new link 25/07/22
    {
      path: "/edbs/:idEdb/entreprise/:idCompany/intervenant/:idChildCompany",
      component: Page.EntrepriseProfile,
      name: "",
      hasAccess: true,
    },
    {
      path: "/edbs/:idEdb/entreprise/:idCompany/agence-transporteur/:idChildCompany",
      component: Page.EntrepriseProfile,
      name: "",
      hasAccess: true,
    },
    {
      path: "/edbs/:idEdb/entreprise/:id",
      component: Page.EntrepriseProfile,
      name: "",
      hasAccess: true,
    },
    // Note : les liens /edbs/:id/XXXX/ doivent etre avant le lien en dessous
    {
      path: "/edbs/:idEdb",
      component: Page.ProfileEDB,
      name: "",
      hasAccess: true,
    },
    {
      path: "/profil",
      component: Page.Profile,
      name: "Profile",
      hasAccess: true,
    },
    {
      path: "/edbs/:idEdb/entreprise/:id",
      component: Page.EntrepriseProfile,
      name: "",
      hasAccess: true,
    },
    {
      path: "/create-edb/:idEdb?",
      component: EDBForm,
      name: "Créer une EDB",
      hasAccess: true,
    },
    {
      path: "/entreprise",
      component: Page.Entreprises,
      name: "Liste des entreprises",
      hasAccess: adminAccess,
    },
    {
      path: "/entreprise/catalogue",
      component: Page.Catalogue,
      name: "",
      hasAccess: true,
    },
    {
      path: "/entreprise/:id",
      component: Page.EntrepriseProfile,
      name: "",
      hasAccess: adminAccess,
    },
    {
      path: "/entreprise/:idCompany/agence-transporteur/:idChildCompany",
      component: Page.EntrepriseProfile,
      name: "",
      hasAccess: adminAccess,
    },
    {
      path: "/entreprise/:idCompany/intervenant/:idChildCompany",
      component: Page.EntrepriseProfile,
      name: "",
      hasAccess: adminAccess,
    },
    {
      path: "/stock",
      component: Page.Stock,
      name: "Liste des stocks",
      hasAccess: true,
    },
    {
      path: "/stock/:idCompany",
      component: Page.CompanyStock,
      name: "",
      hasAccess: true,
    },
    {
      path: "/coordinators",
      component: Page.Coordinators,
      name: "Liste des coordinateurs",
      hasAccess: true,
    },
    {
      path: "/coordinators/:idUser",
      component: Page.Coordinators,
      name: "",
      hasAccess: true,
    },
    {
      path: "/entreprise/:id/projets",
      component: Page.Projects,
      name: "Liste des projets",
      hasAccess: true,
    },
    {
      path: "/entreprise/:id/chantiers",
      component: Page.Dockyards,
      name: "Liste des chantiers",
      hasAccess: true,
    },
    {
      path: "/entreprise/:id/edbs",
      component: Page.EDB,
      name: "Liste des expressions de besoin",
      hasAccess: true,
    },
    {
      path: "/entreprise/:id/entrepot",
      component: Page.Entrepot,
      name: "Liste des entrepôts",
      hasAccess: adminAccess,
    },
    {
      path: "/entreprise/:idCompany/stock",
      component: Page.CompanyStock,
      name: "",
      hasAccess: true,
    },
    {
      path: "/projets",
      component: Page.Projects,
      name: "Liste des projets",
      hasAccess: true,
    },
    {
      path: "/projets/:idProject",
      component: Page.ProjectDetails,
      name: "",
      hasAccess: true,
    },
    {
      path: "/entreprise/:id/projets/:idProject",
      component: Page.ProjectDetails,
      name: "",
      hasAccess: true,
    },
    {
      path: "/chantiers",
      component: Page.Dockyards,
      name: "Liste des chantiers",
      hasAccess: true,
    },
    {
      path: "/chantiers/:idChantier/projets/:idProject",
      component: Page.ProjectDetails,
      name: "",
      hasAccess: true,
    },
    {
      path: "/projets/:idProject/:idChantier",
      component: Page.DockyardDetails,
      name: "",
      hasAccess: true,
    },
    {
      path: "/chantiers/:idChantier",
      component: Page.DockyardDetails,
      name: "",
      hasAccess: true,
    },
    {
      path: "/entreprise/:id/chantiers/:idChantier",
      component: Page.DockyardDetails,
      name: "",
      hasAccess: true,
    },
    // ex-place des liens commençant par "/edbs"
    {
      path: "/chantiers/:idChantier/:idEdb",
      component: Page.ProfileEDB,
      name: "",
      hasAccess: true,
    },
    {
      path: "/projets/:idProject/:idChantier/:idEdb",
      component: Page.ProfileEDB,
      name: "",
      hasAccess: true,
    },
    {
      path: "/entreprise/:id/edbs/:idEdb",
      component: Page.ProfileEDB,
      name: "",
      hasAccess: true,
    },
    {
      path: "/preparations",
      component: Page.Preparation,
      name: "Liste des préparations",
      hasAccess: adminAccess,
    },
    {
      path: "/expeditions",
      component: Page.Shipments,
      name: "Liste des expéditions",
      hasAccess: adminAccess,
    },
    {
      path: "/expeditions/:shippingRecordId",
      component: Page.ShippingRecordDetails,
      name: "",
      hasAccess: adminAccess,
    },
    {
      path: "/configurations",
      component: Page.Configurations,
      name: "",
      hasAccess: true,
    },
    {
      path: "/catalogue",
      component: Page.Catalogue,
      name: "Catalogue",
      hasAccess: true,
    },
    {
      path: "/entreprise/:idCompany/catalogue",
      component: Page.Catalogue,
      name: "",
      hasAccess: true,
    },
    {
      path: "/catalogue/categories",
      component: Page.Category,
      name: "Gérer les catégories",
      hasAccess: true,
    },
    {
      path: "/catalogue/categories/:idCompany",
      component: Page.Category,
      name: "",
      hasAccess: true,
    },
    {
      path: "/catalogue/:idCompany",
      component: Page.Catalogue,
      name: "",
      hasAccess: true,
    },
    {
      path: "/entreprise/:idCompany/catalogue/categories",
      component: Page.Category,
      name: "",
      hasAccess: true,
    },
    {
      path: "/entreprise/:idCompany/catalogue/:idProduct",
      component: Page.Catalogue,
      name: "",
      hasAccess: true,
    },
    {
      path: "/entrepot",
      component: Page.Entrepot,
      name: "Liste des entrepôts",
      hasAccess: adminAccess,
    },
    {
      path: "/entrepot/:idEntrepot",
      component: Page.EntrepotDetails,
      name: "",
      hasAccess: adminAccess,
    },
    {
      path: "/entrepot/:idEntrepot/:idCompany",
      component: Page.EntrepriseProfile,
      name: "",
      hasAccess: true,
    },
    {
      path: "/entreprise/:id/:idEntrepot",
      component: Page.EntrepotDetails,
      name: "",
      hasAccess: adminAccess,
    },
    {
      path: "/entreprise/:id/entrepot/:idEntrepot",
      component: Page.EntrepotDetails,
      name: "",
      hasAccess: adminAccess,
    },
    {
      path: "/commandes",
      component: Page.Orders,
      name: "Liste des commandes",
      hasAccess: adminAccess || clientAccess || contributorAccess,
    },
    {
      path: "/receptions",
      component: Page.Receptions,
      name: "Liste des réceptions",
      hasAccess: adminAccess || clientAccess || contributorAccess,
    },
    {
      path: "/composants-graphiques",
      component: Page.GraphicComponents,
      name: "Composants graphiques",
      hasAccess: true,
    },
    {
      path: "/forbidden",
      component: Page.Forbidden,
      name: "Accès interdit",
      hasAccess: true,
    },
  ];
};

// BreadcrumbProvider to share breadcrumb data accross app with over charging redux
export function BreadcrumbProvider({ children, routerProps }: any) {
  const { isAdmin, isClient, isContributor } = useAuth();

  // we shouldn't recalcul this each times, once we are connect it will never change !
  const links = useMemo(
    () => makeLinks({ isAdmin, isClient, isContributor }),
    [isAdmin, isClient, isContributor]
  );

  const value: IContext = useMemo(
    () => ({
      links,
      routerProps,
    }),
    // see same thing here ! once we are connect we got the right, it won't change live so we don't need this
    [links, routerProps]
  );

  return (
    <BreadcrumbContext.Provider value={value}>
      {children && children}
    </BreadcrumbContext.Provider>
  );
}

// Breadcrumb hooks to consume breadcrumb data
export const useBreadcrumb = () => {
  const value = useContext(BreadcrumbContext);
  return value || { links: [], routerProps: {} };
};

// Breadcrumb component
function Breadcrumb({ title }: any) {
  const { links, routerProps } = useBreadcrumb();
  const location = useLocation();

  const routerParams = routerProps?.match?.params;
  const hasThreeIds = !!(
    routerParams?.idProject &&
    routerParams?.idChantier &&
    routerParams?.idEdb
  );

  const { token, isAdmin } = useAuth();

  // Remove useApi hook and replace with individual queries
  const companyId = routerParams?.id || routerParams?.idCompany;
  const childCompanyId = routerParams?.idChildCompany;
  const projectId = routerParams?.idProject;
  const dockyardId = routerParams?.idChantier;
  const edbId = routerParams?.idEdb;
  const userId = routerParams?.idUser;
  const productId = routerParams?.idProduct;
  const warehouseId = routerParams?.idEntrepot;
  const shippingRecordId = routerParams?.shippingRecordId;
  const opId = routerParams?.opId;

  const { data: companyData } = useQuery({
    queryKey: ["getCompanyById", companyId],
    queryFn: () => CompanyServices.getCompanyById(companyId),
    enabled: !!companyId,
  });

  const { data: childCompanyData } = useQuery({
    queryKey: ["getCompanyById", childCompanyId],
    queryFn: () => CompanyServices.getCompanyById(childCompanyId),
    enabled: !!childCompanyId,
  });

  const { data: warehouseData } = useQuery({
    queryKey: ["getWarehouseById", warehouseId],
    queryFn: () => EntrepotService.getEntrepot(token, warehouseId),
    enabled: !!warehouseId,
  });

  const { data: projectData } = useQuery({
    queryKey: ["getProjectById", projectId],
    queryFn: () => ProjectServices.getProjectById(projectId),
    enabled: !!projectId,
  });

  const { data: dockyardData } = useQuery({
    queryKey: ["getDockyardById", dockyardId],
    queryFn: () => DockyardServices.getDockyardById(dockyardId),
    enabled: !!dockyardId,
  });

  const { data: edbData } = useQuery({
    queryKey: ["getEdbById", edbId],
    queryFn: () => EDBService.getEdbById(edbId),
    enabled: !!edbId,
  });

  const { data: userData } = useQuery({
    queryKey: ["getUserById", userId],
    queryFn: () => UserServices.getUserById(userId),
    enabled: !!userId,
  });

  const { data: productData } = useQuery({
    queryKey: ["getProductById", productId],
    queryFn: () => ProductService.getProductById(productId),
    enabled: !!productId,
  });

  const { data: shippingRecordData } = useQuery({
    queryKey: ["getShippingRecordById", shippingRecordId],
    queryFn: () =>
      ShippingRecordService.getShippingRecord(token, shippingRecordId),
    enabled: !!shippingRecordId,
  });

  const { data: opData } = useQuery({
    queryKey: ["getOpById", opId],
    queryFn: () => OpService.getOpById(opId),
    enabled: !!opId,
  });

  const getLinkData = (id: string) => {
    if (id === dockyardId) return dockyardData;
    if (id === projectId) return projectData;
    if (id === companyId) return companyData;
    if (id === childCompanyId) return childCompanyData;
    if (id === userId) return userData;
    if (id === edbId) return edbData;
    if (id === opId) return opData;
    if (id === warehouseId) return warehouseData;
    if (id === productId) return productData;
    if (id === shippingRecordId) return shippingRecordData;
    return null;
  };

  const classes = useStyles();

  // State-based breadcrumbs: propagated through location.state on each navigation.
  // Takes priority over URL-based resolution to preserve context across unrelated URLs.
  const stateBreadcrumbs = (routerProps?.location?.state as any)
    ?.breadcrumbs as Array<{ path: string; name: string }> | undefined;

  if (stateBreadcrumbs?.length >= 2) {
    if (typeof window !== "undefined") {
      window.document.title = `BATAIL-LOG | ${
        stateBreadcrumbs[stateBreadcrumbs.length - 1]?.name
      }`;
    }
    return (
      <div className={classes.Breadcrumb}>
        {stateBreadcrumbs.map((crumb, index) => (
          <Link
            className={classes.Breadcrumb__Link}
            key={crumb.path}
            to={crumb.path}
            state={{ breadcrumbs: stateBreadcrumbs.slice(0, index + 1) }}
          >
            <Text type="important" className={classes.Breadcrumb_Text}>
              {crumb.name}
            </Text>
            <span className={classes.Breadcrumb__Arrow}>{">"}</span>
          </Link>
        ))}
      </div>
    );
  }

  let crumbs = (links || [])
    .filter((l) => routerProps?.match?.path?.includes(l.path))
    .map(({ path, ...rest }) => ({
      path: Object.keys(routerParams).length
        ? Object.keys(routerParams).reduce(
            (path, param) => path.replace(`:${param}`, routerParams[param]),
            path
          )
        : path,
      ...rest,
    }));

  crumbs = (crumbs || []).filter((c, i) => {
    if (i === 0) return true;
    const cArray = c.path.split("/");
    const last = crumbs[i - 1]?.path.split("/");
    if (last[last.length - 1] === cArray[cArray.length - 1]) return false;
    return true;
  });

  // remove garbage
  crumbs = (crumbs || []).filter((c: any) => !c.path.includes("Chantier"));
  crumbs = (crumbs || []).filter((c: any) => !c.path.includes("Project"));
  crumbs = (crumbs || []).filter((c: any) => !c.path.includes("Edb"));
  crumbs = (crumbs || []).filter((c: any) => !c.path.includes("Entrepot"));

  // hide breadcrumb if home
  const pathname = location?.pathname;
  if (pathname === "/") {
    if (typeof window !== "undefined") {
      window.document.title = "BATAIL-LOG";
    }
    return null;
  }
  // hide if main link
  if (!!crumbs.length && pathname === crumbs[0]?.path) {
    if (typeof window !== "undefined") {
      window.document.title = "BATAIL-LOG";
    }
    return null;
  }
  let id =
    routerParams?.id ||
    routerParams?.idCompany ||
    routerParams?.idProject ||
    routerParams?.idChantier ||
    routerParams?.idEdb ||
    routerParams?.idEntrepot ||
    routerParams?.idUser ||
    routerParams?.idProduct ||
    routerParams?.shippingRecordId ||
    routerParams?.opId ||
    "";
  if (hasThreeIds) {
    id = routerParams?.idProject;
  }

  let secondId =
    routerParams?.idEntrepot ||
    routerParams?.idEdb ||
    routerParams?.idChantier ||
    routerParams?.idProject ||
    routerParams?.idUser ||
    routerParams?.idProduct ||
    routerParams?.idChildCompany ||
    routerParams?.shippingRecordId ||
    routerParams?.opId ||
    routerParams?.id ||
    routerParams?.idCompany ||
    "";

  if (hasThreeIds) {
    secondId = routerParams?.idChantier;
  }
  let thirdId = routerParams?.idEdb;

  let linkTitle = getLinkData(id);
  let secondLink = getLinkData(secondId);
  let thirdLink = getLinkData(thirdId);

  const routePath = routerProps?.match?.path;
  const isEdbCompanyRoute = routePath === "/edbs/:idEdb/entreprise/:id";
  const isCompanyEdbRoute = routePath === "/entreprise/:id/edbs/:idEdb";
  const isEdbCompanyChildRoute =
    routePath ===
      "/edbs/:idEdb/entreprise/:idCompany/intervenant/:idChildCompany" ||
    routePath ===
      "/edbs/:idEdb/entreprise/:idCompany/agence-transporteur/:idChildCompany";
  const useCustomEdbBreadcrumbs =
    isEdbCompanyRoute || isCompanyEdbRoute || isEdbCompanyChildRoute;

  if (useCustomEdbBreadcrumbs) {
    const currentCompanyId = routerParams?.id || routerParams?.idCompany || "";
    const currentEdbId = routerParams?.idEdb || "";
    const currentChildCompanyId = routerParams?.idChildCompany || "";

    if (isEdbCompanyChildRoute) {
      crumbs = [
        {
          path: "/edbs",
          component: Page.EDB,
          name: "Liste des expressions de besoin",
          hasAccess: true,
        },
        {
          path: `/edbs/${currentEdbId}`,
          component: Page.ProfileEDB,
          name: edbData?.edbId || currentEdbId,
          hasAccess: true,
        },
        {
          path: `/edbs/${currentEdbId}/entreprise/${currentCompanyId}`,
          component: Page.EntrepriseProfile,
          name: companyData?.name || currentCompanyId,
          hasAccess: true,
        },
        {
          path: routerProps?.match?.url,
          component: Page.EntrepriseProfile,
          name: childCompanyData?.name || currentChildCompanyId,
          hasAccess: true,
        },
      ];

      linkTitle = undefined;
      secondLink = undefined;
      thirdLink = undefined;
      secondId = undefined;
      thirdId = undefined;

      if (typeof window !== "undefined" && crumbs.length > 0) {
        window.document.title = `BATAIL-LOG | ${
          crumbs[crumbs.length - 1]?.name
        }`;
      }
    } else
      crumbs = isEdbCompanyRoute
        ? [
            {
              path: "/edbs",
              component: Page.EDB,
              name: "Liste des expressions de besoin",
              hasAccess: true,
            },
            {
              path: `/edbs/${currentEdbId}`,
              component: Page.ProfileEDB,
              name: edbData?.edbId || currentEdbId,
              hasAccess: true,
            },
            {
              path: `/edbs/${currentEdbId}/entreprise/${currentCompanyId}`,
              component: Page.EntrepriseProfile,
              name: companyData?.name || currentCompanyId,
              hasAccess: true,
            },
          ]
        : [
            {
              path: "/entreprise",
              component: Page.Entreprises,
              name: "Liste des entreprises",
              hasAccess: isAdmin(),
            },
            {
              path: `/entreprise/${currentCompanyId}`,
              component: Page.EntrepriseProfile,
              name: companyData?.name || currentCompanyId,
              hasAccess: isAdmin(),
            },
            {
              path: `/entreprise/${currentCompanyId}/edbs/${currentEdbId}`,
              component: Page.ProfileEDB,
              name: edbData?.edbId || currentEdbId,
              hasAccess: true,
            },
          ];

    linkTitle = undefined;
    secondLink = undefined;
    thirdLink = undefined;
    secondId = undefined;
    thirdId = undefined;

    if (typeof window !== "undefined" && crumbs.length > 0) {
      window.document.title = `BATAIL-LOG | ${crumbs[crumbs.length - 1]?.name}`;
    }
  }

  if (
    !useCustomEdbBreadcrumbs &&
    crumbs.length === 5 &&
    crumbs[0]?.path === "/edbs"
  ) {
    const oldLinkTitle = linkTitle;
    const oldSecondLink = secondLink;
    linkTitle = oldSecondLink;
    secondLink = oldLinkTitle;

    // contournement
    thirdId = undefined;
    thirdLink = undefined;

    crumbs = [
      {
        path: "/edbs",
        component: Page.EDB,
        name: "Liste des expressions de besoin",
        hasAccess: true,
      },
      crumbs[2],
      crumbs[4],
    ];
  }

  if (crumbs.length === 5 && crumbs[2]?.path === "/chantiers") {
    const oldLinkTitle = linkTitle;
    const oldSecondLink = secondLink;
    linkTitle = oldSecondLink;
    secondLink = oldLinkTitle;

    // contournement
    thirdId = undefined;
    thirdLink = undefined;

    crumbs = [
      {
        path: "/chantiers",
        component: Page.Dockyards,
        name: "Liste des chantiers",
        hasAccess: true,
      },
      {
        ...crumbs[3],
        path: `/chantiers/${routerParams?.idChantier}`,
      },
      crumbs[4],
    ];
  }

  if (crumbs.length === 3 && crumbs[0]?.path === "/entrepot") {
    const a = linkTitle;
    linkTitle = secondLink;
    secondLink = a;
  }
  if (crumbs.length === 3 && crumbs[0]?.path === "/catalogue") {
    const tmps = {
      ...crumbs[2],
      path: "/catalogue/:idCompany",
    };
    crumbs = [crumbs[0], tmps, crumbs[1]];
  }
  if (
    crumbs.length === 4 &&
    (crumbs[3]?.path.includes("import") ||
      crumbs[3]?.path.includes("categories"))
  ) {
    const tmps = {
      ...crumbs[2],
      path: "/entreprise/:idCompany/catalogue",
    };
    crumbs = [crumbs[0], crumbs[1], tmps, crumbs[3]];
  }
  const replaceValue = (data: any, value: string) => {
    if (data.path.includes(":id")) {
      return data.path.replace(":id", value);
    }
    if (data.path.includes(":idCompany")) {
      return data.path.replace(":idCompany", value);
    }
    if (data.path.includes(":idProject")) {
      return data.path.replace(":idProject", value);
    }
    if (data.path.includes(":idChantier")) {
      return data.path.replace(":idChantier", value);
    }
    if (data.path.includes(":idEdb")) {
      return data.path.replace(":idEdb", value);
    }
    if (data.path.includes(":idEntrepot")) {
      return data.path.replace(":idEntrepot", value);
    }
    if (data.path.includes(":idUser")) {
      return data.path.replace(":idUser", value);
    }
    if (data.path.includes(":idProduct")) {
      return data.path.replace(":idProduct", value);
    }
    if (data.path.includes(":idChildCompany")) {
      return data.path.replace(":idChildCompany", value);
    }
    return data.path || "";
  };

  return (
    <div className={classes.Breadcrumb}>
      {crumbs &&
        crumbs.map((link, index) => {
          let name =
            title && link.name === ""
              ? linkTitle?.name ||
                linkTitle?.edbId ||
                linkTitle?.code ||
                linkTitle?.designation ||
                (linkTitle?.lastname
                  ? `${linkTitle?.firstname || ""} ${linkTitle?.lastname || ""}`
                  : undefined) ||
                ""
              : link.name;
          if (index === crumbs.length - 1 && secondId && secondLink) {
            name =
              title && link.name === ""
                ? secondLink?.name ||
                  secondLink?.edbId ||
                  secondLink?.code ||
                  secondLink?.designation ||
                  (secondLink?.lastname
                    ? `${secondLink?.firstname || ""} ${
                        secondLink?.lastname || ""
                      }`
                    : undefined) ||
                  title ||
                  ""
                : link.name;
            if (typeof window !== "undefined") {
              window.document.title = `BATAIL-LOG | ${name}`;
            }
          } else if (secondId && secondLink && hasThreeIds && index === 2) {
            name = secondLink?.name;
            if (typeof window !== "undefined") {
              window.document.title = `BATAIL-LOG | ${name}`;
            }
          }
          if (index === crumbs.length - 1 && thirdId && thirdLink) {
            name = thirdLink.edbId || "";

            if (typeof window !== "undefined") {
              window.document.title = `BATAIL-LOG | ${name}`;
            }
          }
          return (
            <Link
              className={[classes.Breadcrumb__Link, ""].join(" ")}
              key={link.path}
              to={replaceValue(link, id).replace("Company", "")}
            >
              <Text type="important" className={classes.Breadcrumb_Text}>
                {name || ""}
              </Text>
              <span className={classes.Breadcrumb__Arrow}>{">"}</span>
            </Link>
          );
        })}
    </div>
  );
}

export default Breadcrumb;

const useStyles = createUseStyles(() => ({
  Breadcrumb: {
    marginTop: "20px",
    display: "inline-flex",
  },
  Breadcrumb__Link: {
    textDecoration: "none",
    "&:last-child": {
      pointerEvents: "none !important",
      cursor: "default !important",
    },
    "&:last-child span:last-child": {
      display: "none !important",
    },
    "&:last-child span:first-child": {
      color: "#457dd9 !important",
      fontWeight: "700 !important",
    },
  },
  Breadcrumb_Text: {
    fontSize: "13px",
    fontWeight: "400",
    color: `${theme.color.gray5}`,
  },
  Breadcrumb__Arrow: {
    paddingLeft: "5px",
    paddingRight: "5px",
    fontSize: "13px",
    fontWeight: "400",
    color: `${theme.color.gray5}`,
  },
}));
